import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOKEN_KEY = "token";
// sessionStorage is per-tab: a /view-as tab uses its own impersonation token there,
// while the opener tab (empty sessionStorage) keeps using its localStorage admin token.
const getStoredToken = () => sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);

// A /view-as tab keeps its own token in sessionStorage. When switching identity
// inside such a tab, keep the new token there so the tab stays isolated from the
// opener's admin session (getStoredToken reads sessionStorage first).
const persistToken = (value) => {
  try {
    if (sessionStorage.getItem(TOKEN_KEY)) {
      sessionStorage.setItem(TOKEN_KEY, value);
      return;
    }
  } catch (e) {
    /* storage unavailable */
  }
  try {
    localStorage.setItem(TOKEN_KEY, value);
  } catch (e) {
    /* storage unavailable */
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getStoredToken());
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      
      // Check if we're impersonating by decoding token
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      setImpersonating(!!tokenPayload.impersonated_by);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem("token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setImpersonating(false);
    
    return userData;
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setImpersonating(false);
  };

  const impersonate = async (userId) => {
    const response = await axios.post(`${API}/auth/impersonate`, { user_id: userId });
    const { access_token, user: userData } = response.data;

    persistToken(access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setImpersonating(true);
    
    return userData;
  };

  const stopImpersonation = async () => {
    const response = await axios.post(`${API}/auth/stop-impersonation`);
    const { access_token, user: userData } = response.data;

    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.setItem("token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setImpersonating(false);

    return userData;
  };

  const switchCompany = async (companyName) => {
    const response = await axios.post(`${API}/auth/switch-company`, { company_name: companyName });
    setUser(response.data);
    return response.data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      impersonating,
      login,
      logout,
      impersonate,
      stopImpersonation,
      switchCompany,
      fetchUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
